// Same resolution vue-qrcode-reader asks for. 4:3 on purpose: in portrait a
// 16:9 mode crops the sensor's width and the preview looks zoomed in.
const scannerVideo = () => ({
  facingMode: { ideal: 'environment' },
  width: { min: 360, ideal: 640, max: 1920 },
  height: { min: 240, ideal: 480, max: 1080 },
});

let installed = false;
// Counted rather than a flag: the router can mount the next scanner before it
// destroys the previous one, which would otherwise clear the override too early.
let scanners = 0;
// Bumped whenever the scanner camera must go away; a request that resolves
// after a bump is stale and is stopped instead of being kept alive.
let generation = 0;
const streams = new Set();

const stopScannerStreams = () => {
  generation += 1;
  streams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
  streams.clear();
};

// The OS can end a backgrounded app's capture without the page ever getting a
// visibilitychange (Median does not document one), which leaves a frozen
// preview. An externally ended track (`ended` never fires on our own stop())
// marks the camera lost, and the scanner page is asked to restart it on the
// next sign that the app is in the foreground.
export const SCANNER_CAMERA_RESTART_EVENT = 'ce-go:scanner-camera-restart';
const MAX_RESTARTS = 3;
const RESTART_WINDOW_MS = 15000;

let cameraLost = false;
let restarts = [];

const requestRestart = () => {
  if (!cameraLost || scanners === 0 || document.hidden) {
    return;
  }

  const now = Date.now();

  restarts = restarts.filter((time) => now - time < RESTART_WINDOW_MS);

  // A camera that keeps dying (another app holds it) must not restart in a loop.
  if (restarts.length >= MAX_RESTARTS) {
    return;
  }

  restarts.push(now);
  cameraLost = false;
  window.dispatchEvent(new CustomEvent(SCANNER_CAMERA_RESTART_EVENT));
};

const watchStream = (stream) => {
  stream.getVideoTracks().forEach((track) => {
    track.addEventListener('ended', () => {
      if (!streams.delete(stream)) {
        return;
      }

      cameraLost = true;

      // Still in the foreground (e.g. another app briefly took the camera).
      if (document.hasFocus()) {
        requestRestart();
      }
    });
  });
};

// Median calls these globals when the app returns from the background.
const chainAppResumed = (name) => {
  const previous = window[name];

  window[name] = function (...args) {
    requestRestart();

    return typeof previous === 'function' ? previous.apply(this, args) : undefined;
  };
};

// Hidden on-device diagnostics: shows which lens the OS actually opened.
// Enabled by `?camdebug=1` (remembered, `?camdebug=0` clears it) or by five
// quick taps on the scanner preview, since the Median app has no address bar.
const DEBUG_KEY = 'ce-go:camdebug';
const TAPS_TO_TOGGLE = 5;
const TAP_WINDOW_MS = 3000;

let debugEnabled = false;
let panel = null;
let panelTimer = null;
let cameraLabels = [];
let taps = [];

const persistDebug = (enabled) => {
  try {
    if (enabled) {
      localStorage.setItem(DEBUG_KEY, '1');
    } else {
      localStorage.removeItem(DEBUG_KEY);
    }
  } catch (e) {
    // Storage can be unavailable (private mode); the toggle still works for this page.
  }
};

const readDebugFlag = () => {
  try {
    const param = new URLSearchParams(window.location.search).get('camdebug');

    if (param !== null) {
      persistDebug(param === '1');
    }

    return localStorage.getItem(DEBUG_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const liveScannerTrack = () => {
  const tracks = [...streams]
    .flatMap((stream) => stream.getVideoTracks())
    .filter((track) => track.readyState === 'live');

  return { track: tracks[tracks.length - 1], live: tracks.length };
};

const describeCamera = () => {
  const { track, live } = liveScannerTrack();
  const lines = ['Camera diagnostics'];

  if (!track) {
    lines.push('Camera: off');
  } else {
    const settings = track.getSettings ? track.getSettings() : {};
    const zoom = (track.getCapabilities ? track.getCapabilities() : {}).zoom;

    lines.push(
      `Lens: ${track.label || '(no label)'}`,
      `Facing: ${settings.facingMode || 'n/a'}`,
      `Resolution: ${settings.width}×${settings.height} @ ${Math.round(settings.frameRate || 0)} fps`,
      `Zoom: ${settings.zoom != null ? settings.zoom : 'n/a'}${zoom ? ` (range ${zoom.min}–${zoom.max})` : ''}`,
    );
  }

  lines.push(
    `Live scanner streams: ${live}`,
    `Camera lost, waiting to restart: ${cameraLost ? 'yes' : 'no'} (restarts in 15 s: ${restarts.length})`,
    `Cameras on device (${cameraLabels.length}):`,
    ...cameraLabels.map((label) => `  • ${label || '(no label)'}`),
    `UA: ${navigator.userAgent}`,
  );

  return lines.join('\n');
};

const refreshCameraLabels = () => {
  navigator.mediaDevices.enumerateDevices()
    .then((devices) => {
      cameraLabels = devices.filter(({ kind }) => kind === 'videoinput').map(({ label }) => label);
    })
    .catch(() => {});
};

const renderPanel = () => {
  if (panel) {
    panel.querySelector('pre').textContent = describeCamera();
  }
};

const hidePanel = () => {
  clearInterval(panelTimer);
  panelTimer = null;

  if (panel) {
    panel.remove();
    panel = null;
  }
};

const showPanel = () => {
  if (panel) {
    return;
  }

  panel = document.createElement('div');
  panel.setAttribute('role', 'status');
  panel.style.cssText = [
    // Above the scanner's bottom tools, so it never covers its title or close button.
    'position:fixed', 'bottom:calc(env(safe-area-inset-bottom) + 92px)', 'left:8px', 'right:8px', 'z-index:2147483000',
    'max-height:38vh', 'overflow:auto', 'padding:8px 10px', 'border-radius:8px',
    'background:rgba(0,0,0,.82)', 'color:#fff', 'font:11px/1.45 ui-monospace,Menlo,monospace',
  ].join(';');
  panel.innerHTML = '<pre style="margin:0;white-space:pre-wrap;user-select:text;-webkit-user-select:text"></pre>'
    + '<div style="display:flex;gap:8px;margin-top:6px">'
    + '<button type="button" data-action="copy" style="font:inherit;padding:2px 8px">Copy</button>'
    + '<button type="button" data-action="close" style="font:inherit;padding:2px 8px">Close</button></div>';

  panel.addEventListener('click', ({ target }) => {
    const action = target.getAttribute('data-action');

    if (action === 'copy') {
      navigator.clipboard?.writeText(describeCamera()).catch(() => {});
    } else if (action === 'close') {
      setDebug(false);
    }
  });

  document.body.appendChild(panel);
  refreshCameraLabels();
  renderPanel();
  panelTimer = setInterval(renderPanel, 1000);
};

const setDebug = (enabled) => {
  debugEnabled = enabled;
  persistDebug(enabled);

  if (enabled && scanners > 0) {
    showPanel();
  } else {
    hidePanel();
  }
};

const onPreviewTap = ({ target }) => {
  if (scanners === 0 || !target.closest || !target.closest('.qrcode-stream-wrapper')) {
    return;
  }

  const now = Date.now();

  taps = taps.filter((time) => now - time < TAP_WINDOW_MS).concat(now);

  if (taps.length >= TAPS_TO_TOGGLE) {
    taps = [];
    setDebug(!debugEnabled);
  }
};

const install = () => {
  const mediaDevices = navigator.mediaDevices;

  if (installed || !mediaDevices?.getUserMedia) {
    return;
  }

  installed = true;
  document.addEventListener('pointerup', onPreviewTap, true);

  const getUserMedia = mediaDevices.getUserMedia.bind(mediaDevices);

  mediaDevices.getUserMedia = async (constraints) => {
    if (scanners === 0 || !constraints?.video) {
      return getUserMedia(constraints);
    }

    // Only one scanner camera is ever wanted: this request supersedes the rest.
    stopScannerStreams();
    cameraLost = false;

    const requested = generation;
    const stream = await getUserMedia({ ...constraints, video: scannerVideo() });

    if (requested !== generation || scanners === 0) {
      stream.getTracks().forEach((track) => track.stop());
    } else {
      streams.add(stream);
      watchStream(stream);

      if (debugEnabled) {
        refreshCameraLabels();
        renderPanel();
      }
    }

    return stream;
  };

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      requestRestart();
    } else if (scanners > 0) {
      stopScannerStreams();
      // The page re-opens its camera itself when it becomes visible again.
      cameraLost = false;
    }
  });
  window.addEventListener('focus', requestRestart);
  window.addEventListener('pageshow', requestRestart);
  chainAppResumed('median_app_resumed');
  chainAppResumed('gonative_app_resumed');
};

/**
 * Makes every camera request made while a QR scanner is open a fixed request
 * for the rear camera by facing direction, so the OS opens its primary lens at
 * the standard field of view, and makes sure no scanner camera outlives it.
 *
 * Two heuristics upstream guess a deviceId instead, and both can land on the
 * telephoto lens (5x on iPhone 15/16 Pro, Pixel Pro), which makes the scanner
 * so zoomed in that a code must be held feet away:
 * - vue-qrcode-reader 3.2.1 (the last Vue 2 release) takes the LAST
 *   videoinput as soon as it sees more than two of them;
 * - its bundled webrtc-adapter treats a user agent without "Chrome/NN" (a
 *   customised WebView UA) as an old Chrome: it rewrites the request into the
 *   legacy mandatory/optional form with that guess as `sourceId`.
 * The video constraints are therefore replaced, not edited: editing would
 * leave `sourceId` behind, and Chrome rejects legacy and spec keys mixed.
 *
 * The override is installed before the library first opens a camera (the
 * adapter wraps getUserMedia on that first call), so it is the last step
 * before the browser. `ideal` never overconstrains: a desktop with a single
 * webcam still gets that webcam.
 *
 * The library starts a camera asynchronously (~600 ms) and a `camera` change
 * during that start orphans a live stream it never stops. Stale requests are
 * stopped on arrival, and every scanner stream is stopped when the page is
 * hidden or the last scanner goes away. A scanner page must listen for
 * SCANNER_CAMERA_RESTART_EVENT and re-open its camera: it fires when the OS
 * ended the camera and the app is back in the foreground.
 *
 * Release it when the scanner goes away: other camera pickers on the page
 * (Uploadcare, for one) keep their own camera choice.
 */
export const preferPrimaryCameraLens = () => {
  install();
  scanners += 1;
  debugEnabled = readDebugFlag();

  if (debugEnabled) {
    showPanel();
  }
};

export const releasePrimaryCameraLens = () => {
  scanners = Math.max(0, scanners - 1);

  if (scanners === 0) {
    stopScannerStreams();
    cameraLost = false;
    hidePanel();
  }
};
